import { applyLocale } from 'i18n';
import { useLocaleStore } from 'stores/useLocaleStore';
import { useLocaleSwitchStore } from 'stores/useLocaleSwitchStore';
import queryClient from '../../../queryClient';

// The one entry point for a USER-INITIATED language switch (both pickers call
// this; the org-pin path in Common/config/languageAccess stays on bare
// applyLocale — it runs during an org switch, which has its own remount flow).
//
// What the overlay is for: applyLocale's change handler clears the React Query
// cache (locale is part of cache identity once the backend localizes
// responses), so for a moment after a switch every mounted query — including
// /permissions/me, which gates whole routes — is refetching against an empty
// cache. Without a gate the user watches the app fall apart into skeletons and
// permission-denied flashes mid-switch. So: raise the overlay, load the
// resources, commit the locale, then hold the overlay until the refetch wave
// settles.

// Give React a beat after setLocale() commits: the cache clear only starts
// refetches once subscribers re-render and their observers remount, so an
// immediate isFetching() reads 0 before the wave has begun.
const REFETCH_START_MS = 150;
const REFETCH_POLL_MS = 100;
// Hard ceiling — a hung request must degrade to "app visible with spinners",
// never to a permanent overlay. Queries retry twice with backoff, so this is
// generous but bounded.
const REFETCH_TIMEOUT_MS = 10_000;
// Floor so an instant switch (English, everything cached) reads as a
// deliberate transition instead of a one-frame flash.
const MIN_VISIBLE_MS = 450;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForRefetchesToSettle() {
  await delay(REFETCH_START_MS);
  const startedAt = Date.now();
  while (queryClient.isFetching() > 0 && Date.now() - startedAt < REFETCH_TIMEOUT_MS) {
    await delay(REFETCH_POLL_MS);
  }
}

export async function switchLocale(code) {
  const { locale, setLocale } = useLocaleStore.getState();
  const { pendingLocale, begin, finish } = useLocaleSwitchStore.getState();
  // Re-entrancy guard: the overlay blocks pointer events, but a queued keyboard
  // selection can still land while a switch is in flight.
  if (pendingLocale || code === locale) return;

  begin(code);
  const shownAt = Date.now();
  try {
    // Ordering matters: applyLocale() loads the resources and the dayjs locale
    // BEFORE the store update re-renders subscribers. Flipping these two makes
    // the whole UI flash through English on every switch.
    await applyLocale(code);
    setLocale(code);
    await waitForRefetchesToSettle();
    const remaining = MIN_VISIBLE_MS - (Date.now() - shownAt);
    if (remaining > 0) await delay(remaining);
  } catch (error) {
    // Same degradation contract as the i18n boot path: a failed bundle load
    // leaves the previous language fully working — never a stuck overlay, and
    // never an unhandled rejection out of a Radix onValueChange.
    console.error('[i18n] language switch failed', error);
  } finally {
    finish();
  }
}
